import { Box, Flex, Text, Textarea } from '@modusign/design-system';
import { DropdownOption, FontType } from '@modusign/react-signing/types';
import {
  ChangeEvent,
  forwardRef,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { DeepReadonly } from 'ts-essentials';

import { OnOffButton } from '@app/components/Buttons';
import {
  ContextMenuGroup,
  ContextMenuRow,
  ContextMenuRowDivider,
  ContextMenuWrapper,
} from '@app/components/ContextMenu';
import ContextMenuRowParticipantName from '@app/components/ContextMenuRowParticipantName';
import DataLabelInput from '@app/components/DataLabelInput';
import {
  FONT_SIZES,
  FONT_TYPES_MAP,
  SELECTABLE_FONT_TYPE,
} from '@app/components/TextField/constants';
import ContextMenuSelect from '@app/features/apply-react-signing/components/FieldSetupStep/ContextMenuSelect';

function textToOptions(text: string, delimiter: string): DropdownOption[] {
  return text
    .trim()
    .split(delimiter)
    .map((option) => option.trim())
    .filter((option) => option.length > 0)
    .map<DropdownOption>((o) => ({ value: o }));
}

export interface DropdownFieldContextMenuProps {
  dataLabel: string;
  dataLabelErrorMessage: string;
  optionsValidationMessage: string;
  fontFamily: FontType;
  fontSize: number;
  isRequired: boolean;
  isDirty: boolean;
  options: DeepReadonly<DropdownOption[]>;
  participantName?: string;
  participantOrder?: number;
  isDataLabelEditable?: boolean;
  delimiter?: string;
  onDirty: (isDirty: boolean) => void;
  onDataLabelChange: (value: string) => void;
  onFontFamilyChange: (fontFamily: FontType) => void;
  onFontSizeChange: (fontSize: number) => void;
  onRequiredToggle: (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    nextIsRequired: boolean
  ) => void;
  onOptionsChange: (options: DropdownOption[]) => void;
}

export default function DropdownFieldContextMenu({
  dataLabel,
  dataLabelErrorMessage,
  optionsValidationMessage,
  fontFamily,
  fontSize,
  isRequired,
  isDirty,
  participantName,
  participantOrder,
  isDataLabelEditable,
  options,
  delimiter = ';',
  onDirty,
  onDataLabelChange,
  onFontFamilyChange,
  onFontSizeChange,
  onRequiredToggle,
  onOptionsChange,
}: DropdownFieldContextMenuProps) {
  const [text, setText] = useState<string>(() => options.map((o) => o.value).join(`${delimiter} `));

  const showIsInvalid = optionsValidationMessage && isDirty;

  const handleFontFamilyChange = useCallback(
    (value: FontType) => {
      onFontFamilyChange(value);
    },
    [onFontFamilyChange]
  );

  const handleFontSizeChange = useCallback(
    (size: number) => {
      onFontSizeChange(size);
    },
    [onFontSizeChange]
  );

  const handleRequiredChange = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      onRequiredToggle(e, !isRequired);
    },
    [isRequired, onRequiredToggle]
  );

  const handleOptionsChanged = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.currentTarget;
      onDirty(true);
      const splicedValue = value.replace(new RegExp(`[^${delimiter}]{51,}`, 'g'), (matched) =>
        matched.substring(0, 50)
      );
      // 개행문자는 select 표준에 의해 제거됩니다.
      // FYI: https://github.com/modusign/modusign-client/pull/9981
      setText(splicedValue.replace(/[\s]+/g, ' '));

      const options = textToOptions(splicedValue, delimiter);
      onOptionsChange(options);
    },
    [delimiter, onDirty, onOptionsChange]
  );

  return (
    <ContextMenuWrapper>
      {participantName && participantOrder !== undefined && (
        <>
          <ContextMenuRowParticipantName
            participantName={participantName}
            participantOrder={participantOrder}
          />
          <ContextMenuRowDivider />
        </>
      )}
      <ContextMenuRow>
        <b>필수 입력</b>
        <OnOffButton
          active={isRequired}
          data-testid="dropdown-field-required-btn"
          style={{ float: 'right' }}
          onClick={handleRequiredChange}
        />
      </ContextMenuRow>
      <ContextMenuRowDivider />
      <ContextMenuRow>
        <ContextMenuGroup>
          <ContextMenuSelect
            menuName="글자 크기"
            options={useMemo(
              () => FONT_SIZES.map((size) => ({ value: size, displayText: `${size}pt` })),
              []
            )}
            selectBoxProps={{
              width: '84px',
            }}
            selectedOptionIndex={FONT_SIZES.indexOf(fontSize)}
            onOptionChange={(option) => handleFontSizeChange(option.value)}
          />
          <ContextMenuSelect
            menuName="글자체"
            options={useMemo(
              () =>
                SELECTABLE_FONT_TYPE.map((type) => ({
                  value: type,
                  displayText: FONT_TYPES_MAP[type],
                })),
              []
            )}
            selectBoxProps={{
              width: '84px',
            }}
            selectedOptionIndex={SELECTABLE_FONT_TYPE.indexOf(fontFamily)}
            onOptionChange={(option) => handleFontFamilyChange(option.value)}
          />
        </ContextMenuGroup>
      </ContextMenuRow>
      <ContextMenuRowDivider />
      <Box py={4} textAlign="left">
        <Flex alignItems="center" justifyContent="left" mt={2}>
          <Box as="b">드롭다운 옵션</Box>
          {options.length > 0 && <Badge>{options.length}</Badge>}
        </Flex>
        <Textarea
          height="96px"
          mt={2}
          placeholder={`옵션을 ${delimiter} 으로 구분해 주세요.`}
          sx={{
            resize: 'none',
            borderColor: showIsInvalid ? 'textNegativeBase' : '',
            borderStyle: 'solid',
            borderWidth: '1px',
            '&:focus': {
              borderColor: showIsInvalid ? 'textNegativeBase' : '',
            },
          }}
          value={text}
          width="100%"
          onBlur={() => onDirty(true)}
          onChange={handleOptionsChanged}
        />
        {showIsInvalid && (
          <Text color="textNegativeBase" fontSize="sm" fontWeight="regular" lineHeight="18px">
            {optionsValidationMessage}
          </Text>
        )}
      </Box>
      {isDataLabelEditable && (
        <>
          <ContextMenuRowDivider />
          <Box pb="20px" pt="10px" textAlign="left">
            <DataLabelInput
              errorMessage={dataLabelErrorMessage}
              value={dataLabel}
              onChange={onDataLabelChange}
            />
          </Box>
        </>
      )}
    </ContextMenuWrapper>
  );
}

const Badge = forwardRef(({ children }: { children: ReactNode }, ref: Ref<HTMLDivElement>) => {
  return (
    <Box
      backgroundColor="statusBackgroundNeutralBase"
      borderRadius="10px"
      color="textNeutralLight"
      display="inline-block"
      fontSize="10px"
      fontWeight="medium"
      height="16px"
      lineHeight="150%"
      minWidth="auto"
      ml={1}
      p="0px 6px 3px"
      ref={ref}
      textAlign="center"
    >
      {children}
    </Box>
  );
});
